/**
 * @file 用于引导 100、 107 用户做实名认证的弹窗
 * @author edwardzhao、alfielv、malcolmmzma
 */

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { Dialog, Viewer, Button } from '@tencent/spaui';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';

import globalStore from '@/stores';
import { hasFeatureFlag, isFreeLogin } from '@/services';
import { getRainbowCommonConfig } from '@/api/rainbow';
import {
  newRealNameQRCode,
  getUserCert,
  bmRealNameQRCode,
} from '@/api/operator/cgi/agm/index';
import { handleErrResponse } from '@/pages/agency/utils/util';
import { SignFrameStatus, UserVerifyAuthStatus } from '@/model/agency';
import { computeGetUrl } from '@/utils';
import { REACT_APP_PUBLIC_URL } from '@/constants/Constants';

import { superAdminContent, adminContent } from './constants';

import './index.less';

const isProduction = process.env.DEPLOY_ENV === 'production';

const ROLE_CONTENT_MAP = {
  100: superAdminContent,
  117: adminContent,
};

enum EnumApprovalStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  EMPTY = 'EMPTY',
}

interface IUserCertInfo {
  role: number;
  certList: any[];
  approvalStatus: EnumApprovalStatus;
}

// 未实名认证弹窗
const NotRealNameVerifyDialog = (props: {
  userId: number;
  onClose: () => void;
}) => {
  const { userId, onClose } = props;

  const [realNameQR, setRealNameQR] = useState('');

  useEffect(() => {
    // 扫码认证后到认证状态生效（/user/get获取）需要处理时间。
    // 此时如果再次调用newRealNameQRCode接口，会返回30002错误码来表示“已完成实名认证”。
    // 故手动处理此错误码，不再展示全局错误弹窗。
    newRealNameQRCode(
      {
        userId,
        authType: 'WX_PAY',
        validPeriod: 60 * 60 * 48,
      },
      false,
    ).then((rsp: any) => {
      const { code, data } = rsp || {};
      if (code === 0) {
        setRealNameQR(data?.qr_code);
      } else if (code === 30002) {
        setRealNameQR('');
      } else {
        handleErrResponse(rsp);
      }
    });
  }, [userId]);

  return realNameQR ? (
    <Dialog
      title="员工实名认证"
      size="small"
      onClose={onClose}
      onSubmit={() => window.location.reload()}
    >
      <div className="real-name-guide-dialog">
        <img src={`data:image/png;base64,${realNameQR}`} className="qr-img" />
        <span className="tips">请使用微信扫码完成员工实名认证</span>
      </div>
    </Dialog>
  ) : null;
};

const UserVerifyAuthStatusDesc: any = {
  [UserVerifyAuthStatus.PENDING]: '已提交组织认证申请，请联系行业销售审核',
  [UserVerifyAuthStatus.REJECTED]: '组织认证申请被审核驳回',
};
const UserVerifyAuthStatusDialog = (props: {
  cert_status: UserVerifyAuthStatus;
  reject_reason?: string;
  setReApplySvip: (value: boolean) => void;
  closeable: boolean;
  onClose: () => void;
}) => {
  const { cert_status, reject_reason, setReApplySvip, closeable, onClose } = props;

  const handleSubmit = useCallback(() => {
    if (cert_status === UserVerifyAuthStatus.PENDING) {
      window.location.reload();
    } else if (cert_status === UserVerifyAuthStatus.REJECTED) {
      setReApplySvip(true);
    }
  }, [cert_status, setReApplySvip]);

  if (!UserVerifyAuthStatusDesc[cert_status]) {
    return null;
  }

  return <Dialog
    useNewUI={true}
    size="middle"
    title="员工实名认证"
    onSubmit={closeable && cert_status !== UserVerifyAuthStatus.REJECTED ? onClose : handleSubmit}
    submitLabel={cert_status === UserVerifyAuthStatus.REJECTED ? '重新申请' : '我知道了'}
    onClose={onClose}
    closeButton={closeable}
    cancelButton={false}
    hideHeaderDivider={true}
    hideFooterDivider={true}
    footerRightPosition="center"
  >
    <>{cert_status === UserVerifyAuthStatus.REJECTED ? `${UserVerifyAuthStatusDesc[cert_status]}${reject_reason ? `，驳回原因:${reject_reason}` : ''}` : UserVerifyAuthStatusDesc[cert_status]}</>
  </Dialog>;
};

// svip与非框未实名认证弹窗
const NonFrameNotRealNameVerifyDialog = (props: {
  userId: number;
  agentGroup: string;
  wechat_number: string;
  isSvip: boolean;
  status?: SignFrameStatus;
  deadline: string;
  closeable: boolean;
  onClose: () => void;
}) => {
  const { userId, wechat_number, status, isSvip, agentGroup, deadline, closeable, onClose } = props;

  const [realNameQR, setRealNameQR] = useState('');

  useEffect(() => {
    // 扫码认证后到认证状态生效需要处理时间。
    // 此时如果再次调用bmRealNameQRCode接口，会返回30002错误码来表示“已完成实名认证”。
    // 故手动处理此错误码，不再展示全局错误弹窗。
    bmRealNameQRCode(
      {
        userId,
        authType: 'WX_PAY',
        validPeriod: 60 * 60 * 48,
        isSvip,
        agentGroup,
        signFrameStatus: status,
      },
      false,
    ).then((rsp: any) => {
      const { code, data } = rsp || {};
      if (code === 0) {
        setRealNameQR(data?.qr_code);
      } else if (code === 30002) {
        setRealNameQR('');
      } else {
        handleErrResponse(rsp);
      }
    });
  }, [userId, isSvip, agentGroup, status]);

  const handleSubmit = useCallback(() => {
    window.location.reload();
  }, []);

  return realNameQR ? (
    <Dialog
      title="员工实名认证"
      size="small"
      onSubmit={closeable ? onClose : handleSubmit}
      onClose={onClose}
      closeButton={closeable}
      cancelButton={false}
    >
      <div className="real-name-guide-dialog non-frame">
        <div>按照平台统一要求，用户使用服务商系统必须实名认证。{deadline ? `${dayjs(deadline).format('YYYY年MM月DD日')}后，未认证的用户将无法使用服务商系统。` : ''}请使用微信扫码完成认证。</div>
        <img src={`data:image/png;base64,${realNameQR}`} className="qr-img" />
        {!isSvip ? <div>详见<a href="https://doc.weixin.qq.com/doc/w3_APkAbAaEACgCNpIBFwEiqRUW8ByW0?scode=AJEAIQdfAAoIR7bwi3APkAbAaEACg" target="_blank" rel="noreferrer">《操作手册和常见问题》</a></div> : null}
        {!wechat_number ? <div className="flex items-center"><span className="red">您的个人信息中未绑定微信号，请绑定后再扫码认证</span><Button displayType="link" onClick={() => window.open(computeGetUrl(`${REACT_APP_PUBLIC_URL}/agency/personal-center`))}>前往绑定</Button></div> : null}
      </div>
    </Dialog>
  ) : null;
};

// 员工资质未审核通过弹窗
const EmployeeNotApprovalDialog = (props: {
  onClose: () => void;
  userCertInfo: IUserCertInfo;
}) => {
  const { onClose, userCertInfo } = props;
  const { role, approvalStatus } = userCertInfo;

  const [imageShow, setImageShow] = useState(false);

  // 根据role id获取对应的弹窗内容
  const dialogContent = _.get(
    _.get(ROLE_CONTENT_MAP, role, {}),
    'verified.cert',
    null,
  );

  if (!dialogContent) {
    return null;
  }

  const message = useMemo(() => {
    // user在当前的bm下不存在注册资质记录
    if (approvalStatus === EnumApprovalStatus.EMPTY) {
      return dialogContent.empty;
    }
    if (role === 100) {
      if (approvalStatus === EnumApprovalStatus.REJECTED) {
        return dialogContent.fulfilled.anyRejected;
      }
      if (approvalStatus === EnumApprovalStatus.PENDING) {
        return dialogContent.fulfilled.pending;
      }
    }
    return null;
  }, []);

  // 100已实名，但员工在所属公司下未审核通过（包括无记录）；或117已实名，但不存在注册资质纪录
  return message ? (
    <Dialog
      className="not-real-name-verify-dialog"
      title="员工企业身份认证"
      onClose={onClose}
      cancelButton={false}
      submitLabel="我知道了"
    >
      <div className="not-real-name-verify-dialog-body">
        <p>{message.content}</p>
        {message.imageUrl && (
          <div>
            <Button displayType="link" onClick={() => setImageShow(true)}>
              点击查看操作引导
            </Button>
            <Viewer
              show={imageShow}
              onClose={() => setImageShow(false)}
              data={[{ name: '操作引导', url: message.imageUrl }]}
              index={0}
              type="images"
              download={true}
              footShow={false}
              dragImage={true}
            />
          </div>
        )}
      </div>
    </Dialog>
  ) : null;
};

const CLOSE_STORAGE_KEY = 'REAL_NAME_GUIDE_CLOSE';
const RealNameGuideDialog: React.FC<any> = observer((_props) => {
  const { user_id: userId, wechat_number, real_name_verify_status, userVerifyInfo, bm_id, agency_uid, all_agency_list } = globalStore.account || {};
  const { agentGroup, isSvip, isNonFrame, cert_status, sign_frame_status, reject_reason } = userVerifyInfo || {};
  // 关闭dialog
  const [closed, setClosed] = useState(false);
  const [storageClosed, setStorageClosed] = useState(Boolean(window.sessionStorage.getItem(CLOSE_STORAGE_KEY)));
  // 是否实名认证过
  // const [hasRealNameVerify, setHasRealNameVerify] = useState<null | boolean>(null);
  // 员工在所属公司下是否全部审核通过
  const [userCertInfo, setUserCertInfo] = useState<IUserCertInfo | null>(null);

  // svip重新申请
  const [reApplySvip, setReApplySvip] = useState<boolean>(false);
  // svip对话框是否可关闭
  const [svipWhitelist, setSvipWhitelist] = useState<Record<string, string[]>>({});
  const [svipCloseable, setSvipCloseable] = useState<boolean>(true);
  // 非框对话框是否可关闭
  const [nonFrameWhitelist, setNonFrameWhitelist] = useState<Record<string, number[]>>({});
  const [nonFrameCloseable, setNonFrameCloseable] = useState<boolean>(true);
  const [nonFrameDeadline, setNonFrameDeadline] = useState<string>('');

  // dialog是否可见
  const mayShow = !closed
    && Boolean(userId)
    && hasFeatureFlag('is_enable_strict_employee_rule')
    && Boolean(globalStore.account?.role_list?.['100']); // globalStore.account?.role_list?.['117']

  // 是否命中新灰度码：命中后不再根据 user_auth_deadline 限制实名弹窗关闭
  const isCheckUserForbidNewLogic = hasFeatureFlag('is_check_user_forbid_new_logic');

  useEffect(() => {
    // getUser(userId).then((res) => {
    //   const { code, data } = res || {};
    //   if (code === 0) {
    //     setHasRealNameVerify(data?.real_name_verify_status === 1);
    //   }
    // });

    getUserCert(userId).then((res) => {
      const { code, data } = res || {};
      const { list } = data || {};
      if (code === 0) {
        const roleId = (() => {
          const roleList = _.keys(globalStore.account?.role_list);
          if (roleList.includes('100')) {
            return 100;
          }
          if (roleList.includes('117')) {
            return 117;
          }
          return 0;
        })();
        let approvalStatus = EnumApprovalStatus.EMPTY;
        if (_.isEmpty(list)) {
          setUserCertInfo({
            role: roleId,
            certList: [],
            approvalStatus,
          });
          return;
        }
        if (_.some(list, (item) => item?.approval_status === 2)) {
          // 员工在所有所属公司下任一审核拒绝
          approvalStatus = EnumApprovalStatus.REJECTED;
        } else if (_.every(list, (item) => item?.approval_status === 1)) {
          // 员工在所有所属公司下都审核通过
          approvalStatus = EnumApprovalStatus.APPROVED;
        } else if (_.every(list, (item) => item?.approval_status === 0)) {
          // 员工在所有所属公司下都待审核
          approvalStatus = EnumApprovalStatus.PENDING;
        }
        setUserCertInfo({
          role: roleId,
          certList: list,
          approvalStatus,
        });
      }
    });
  }, [userId]);

  // 关门名单
  useEffect(() => {
    getRainbowCommonConfig('user_auth_deadline').then((config) => {
      setSvipWhitelist(config.svip || {});
      setNonFrameWhitelist(config.nonFrame || {});
    });
  }, []);

  // svip是否关门
  useEffect(() => {
    if (!agentGroup || _.isEmpty(svipWhitelist)) {
      return;
    }
    let dealine = '';
    Object.keys(svipWhitelist).some((key: string) => {
      if (svipWhitelist[key].includes(agentGroup)) {
        dealine = key;
        return true;
      }
      return false;
    });
    // 有设置关门日期（命中新灰度码时，不再根据关门日期限制关闭）
    if (dealine && !isCheckUserForbidNewLogic) {
      setSvipCloseable(dayjs().isBefore(dealine));
    }
  }, [agentGroup, svipWhitelist, isCheckUserForbidNewLogic]);

  // 所有bm_id和agency_uid
  const allIds = useMemo(() => {
    return _.uniq([...(all_agency_list?.map?.((item) => item.agency_uid) || []), bm_id, agency_uid]);
  }, [all_agency_list, bm_id, agency_uid]);

  // 非框是否关门
  useEffect(() => {
    if (_.isEmpty(allIds) || _.isEmpty(nonFrameWhitelist)) {
      return;
    }
    let dealine = '';
    Object.keys(nonFrameWhitelist).some((key: string) => {
      if (_.intersection(allIds, nonFrameWhitelist[key]).length > 0) {
        dealine = key;
        return true;
      }
      return false;
    });
    // 有设置关门日期
    if (dealine) {
      setNonFrameDeadline(dealine);
      // 命中新灰度码时，不再根据关门日期限制关闭
      if (!isCheckUserForbidNewLogic) {
        setNonFrameCloseable(dayjs().isBefore(dealine));
      }
    }
  }, [allIds, nonFrameWhitelist, isCheckUserForbidNewLogic]);

  const onClose = useCallback(() => setClosed(true), []);
  const onStorageClose = useCallback(() => {
    setStorageClosed(true);
    window.sessionStorage.setItem(CLOSE_STORAGE_KEY, '1');
  }, []);

  const { pathname } = useLocation();
  const isActivePath = useMemo(
    () => pathname === '/organization/employee' || pathname === '/home',
    [pathname],
  );
  const isFreePath = useMemo(
    () => pathname === '/agency/personal-center',
    [pathname],
  );

  // 使用effect监听路由变化，重置dialog状态
  useEffect(() => {
    setClosed(false);
  }, [pathname]);


  if (Boolean(userId) && hasFeatureFlag('is_enable_strict_employee_rule')) {
    // 签框服务商

    // 非指定路径下，不展示
    if (!isActivePath) {
      return null;
    }
    // 不满足逻辑前置条件，或未获取到实名接口返回
    if (!mayShow || _.isNil(real_name_verify_status) || _.isNil(userCertInfo)) {
      return null;
    }

    // 未实名
    if (!real_name_verify_status) {
      return <NotRealNameVerifyDialog userId={userId} onClose={onClose} />;
    }
  } else if (isSvip) {
    // svip服务商

    if (storageClosed || !agentGroup || _.isNil(cert_status) || (isProduction && isFreeLogin()) || isFreePath) {
      return null;
    }

    // 未实名认证
    if (!real_name_verify_status || reApplySvip || cert_status === UserVerifyAuthStatus.NOTSUBMIT) {
      return <NonFrameNotRealNameVerifyDialog userId={userId} wechat_number={wechat_number} agentGroup={agentGroup} isSvip={true} deadline={nonFrameDeadline} closeable={svipCloseable} onClose={onStorageClose} />;
    }
    // svip实名认证状态
    return <UserVerifyAuthStatusDialog cert_status={cert_status} reject_reason={reject_reason} setReApplySvip={setReApplySvip} closeable={svipCloseable} onClose={onStorageClose} />;
  } else if (isNonFrame) {
    // 非框服务商
    if (storageClosed || !agentGroup || _.isNil(cert_status) || (isProduction && isFreeLogin()) || isFreePath) {
      return null;
    }

    // 未实名认证
    if (!real_name_verify_status || cert_status !== UserVerifyAuthStatus.APPROVED) {
      return <NonFrameNotRealNameVerifyDialog userId={userId} wechat_number={wechat_number} agentGroup={agentGroup} isSvip={false} status={sign_frame_status} deadline={nonFrameDeadline} closeable={nonFrameCloseable} onClose={onStorageClose} />;
    }
  }

  // TODO: 暂时kill掉所有审核未通过弹窗弹窗
  // 员工在所属公司下未审核通过（包括无记录）
  // if (userCertInfo.approvalStatus !== EnumApprovalStatus.APPROVED) {
  //   return <EmployeeNotApprovalDialog onClose={onClose} userCertInfo={userCertInfo} />;
  // }

  return <></>;
});

export default React.memo(RealNameGuideDialog);
